import { type PropsWithChildren } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import dynastyIcon from '../../assets/loyalty-program-v2/tiers/dynastyIcon.png';
import { LoyaltyTutorialSteps } from '../../features/loyaltyProgramTutorial/constants';
import TutorialVipClubStatus from '../../features/loyaltyProgramTutorial/TutorialVipClubStatus/TutorialVipClubStatus';
import { useLoyaltyProgramTutorial } from '../../features/loyaltyProgramTutorial/useLoyaltyProgramTutorial';
import { analytics } from '../../services/analytics';
import { useFlag } from '../../services/launchDarkly';
import { useStores } from '../../stores/useStores';
import Avatar from '../FullPageDialogs/AccountDialog/Avatar';
import { ACCOUNT_RELATED_DIALOGS } from '../FullPageDialogs/config';
import HomeIcon from '../SVGIcons/HomeIcon/HomeIcon';

import styles from './Headers.module.scss';
import { OldDesignHeaderCrownBadge } from './OldDesignHeaderCrownBadge/OldDesignHeaderCrownBadge';

const DEFAULT_PROGRESS_COLOR = '#808080';

const InnerCircle = (
  props: PropsWithChildren<{ progress: number; color?: string; backgroundColor?: string; className?: string }>,
) => (
  <div
    className={classNames(styles.innerCircle, props.className)}
    style={{
      '--progress-height': `${props.progress}%`,
      '--progress-color': props.color || DEFAULT_PROGRESS_COLOR,
      backgroundColor: props.backgroundColor,
    }}
  >
    {props.children}
  </div>
);

const HeaderCrownBadge = observer(() => {
  const { loyaltyProgramStore, uiStore, userStore } = useStores();
  const { profile: userProfile } = userStore;
  const { showLoyaltyTutorial, tooltipStyles, setPopperElement, setReferenceElement } = useLoyaltyProgramTutorial(
    LoyaltyTutorialSteps.CHECK_CURRENT_STATUS,
    'bottom',
    [110, 22],
  );

  const { currentTier, points } = loyaltyProgramStore.userLoyalty ?? {};
  const hasVipHost = userProfile?.hasVipHost;

  const progressPercentage = ((points || 0) / (currentTier?.pointsToComplete || 0)) * 100;
  const isAccountOpened = uiStore.openFullPageDialog && ACCOUNT_RELATED_DIALOGS.includes(uiStore.openFullPageDialog);

  const badgeClickHandler = () => {
    if (isAccountOpened) {
      uiStore.closeFullPageDialog();
    } else {
      if (loyaltyProgramStore.tutorialStep === LoyaltyTutorialSteps.CHECK_CURRENT_STATUS) {
        uiStore.setOpenFullPageDialog('loyalty_program', { from: 'header' });
        loyaltyProgramStore.setTutorialNextStep();
      } else {
        uiStore.setOpenFullPageDialog('account');
      }
    }
  };

  const onClickBadge = () => {
    if (currentTier) {
      void analytics.sendEvent('loyalty_crown_button', {
        loyalty_club: currentTier.key,
        loyalty_percentage: progressPercentage,
      });
    }

    badgeClickHandler();
  };

  const progress = showLoyaltyTutorial ? 50 : progressPercentage;

  return (
    <>
      <div
        ref={setReferenceElement}
        className={classNames(styles.crownBadge, {
          [styles.crownBadgeTutorial]: showLoyaltyTutorial,
          [styles.badgeBorder]: !isAccountOpened,
          [styles.dynastyBadgeBorder]: !isAccountOpened && hasVipHost,
        })}
        data-testid="crownBadge"
        onClick={onClickBadge}
      >
        {isAccountOpened ? (
          <HomeIcon />
        ) : (
          <div>
            <InnerCircle
              backgroundColor={currentTier?.color || DEFAULT_PROGRESS_COLOR}
              color={currentTier?.color}
              progress={progress}
            >
              <div className={styles.avatarIconWrapper}>
                {userProfile?.profileImage ? (
                  <img
                    alt="avatar"
                    src={userProfile.profileImage}
                    style={{ background: currentTier?.color || DEFAULT_PROGRESS_COLOR }}
                  />
                ) : (
                  <Avatar
                    borderColor={'transparent'}
                    borderWidth={0}
                    color={currentTier?.color}
                    height={'100%'}
                    width={'100%'}
                  />
                )}
              </div>
            </InnerCircle>
            {hasVipHost && <img alt="dynasty" className={styles.dynastyIconImage} src={dynastyIcon} />}
          </div>
        )}
      </div>
      {showLoyaltyTutorial && (
        <TutorialVipClubStatus
          handleClick={badgeClickHandler}
          setPopperElement={setPopperElement}
          tooltipStyles={tooltipStyles}
        />
      )}
    </>
  );
});

function HeaderCrownBadgeSwitch() {
  const redesignAccountIcon = useFlag('ui_redesign_top_bar_account_icon', true);
  if (!redesignAccountIcon) {
    return <OldDesignHeaderCrownBadge />;
  }
  return <HeaderCrownBadge />;
}

export default HeaderCrownBadgeSwitch;
